import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { getTripPlaces } from '../../../api/trip-places/get-trip-places'
import { getTrips } from '../../../api/trips/get-trips'
import { TripTypeEnum, tripTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripInput } from '../../../lib/types/models/trip/trip-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<TripInput> = z
  .object({
    type: createEnumSchema({ fieldName: 'type de voyage', enumType: TripTypeEnum, nullish: true }),
    countries: createNumberArraySchema('pays', {
      minimumNumberOfItems: 1
    }).nullish(),
    // make these fields nullish so the cross-field validation lives in superRefine
    transportMeansId: createNumberSchema('moyen de transport', { nullish: true }),
    arrival: createStringSchema("lieu d'arrivée", { nullish: true }),
    departure: createStringSchema('lieu de départ', { nullish: true }),
    hasTransport: z.boolean(),
    linkedToMainTripId: createNumberSchema('réservation mère', { nullish: true }),
    foreignProviderArrivalPlaceId: createNumberSchema(
      'adresse d’arrivée pour le prestataire étranger',
      { nullish: true }
    ),
    journeyPlaceId: createNumberSchema('lieu de séjour', { nullish: true })
  })
  .superRefine((arg, ctx) => {
    // Validate required single-field values as part of centralised validation
    if (!arg.type) {
      ctx.addIssue({
        path: ['type'],
        code: z.ZodIssueCode.custom,
        message: 'Veuillez sélectionner un type de voyage'
      })
    }

    if (!arg.countries || arg.countries.length === 0) {
      ctx.addIssue({
        path: ['countries'],
        code: z.ZodIssueCode.custom,
        message: 'Veuillez sélectionner au moins un pays de séjour'
      })
    }
    // Enforce consistency between hasTransport and related fields mirroring backend logic

    // transportMeans presence rules
    if (arg.hasTransport && !arg.transportMeansId) {
      ctx.addIssue({
        path: ['transportMeansId'],
        code: z.ZodIssueCode.custom,
        message: 'Veuillez sélectionner un moyen de transport'
      })
    }

    if (!arg.hasTransport && !!arg.transportMeansId) {
      ctx.addIssue({
        path: ['transportMeansId'],
        code: z.ZodIssueCode.custom,
        message: "Le moyen de transport ne doit pas être fourni lorsqu'il n'y a pas de transport"
      })
    }

    if (arg.hasTransport) {
      // When transport is enabled, arrival and departure must be provided
      if (!arg.arrival || !arg.departure) {
        if (!arg.departure) {
          ctx.addIssue({
            path: ['departure'],
            code: z.ZodIssueCode.custom,
            message: 'Veuillez renseigner le lieu de départ'
          })
        }
        if (!arg.arrival) {
          ctx.addIssue({
            path: ['arrival'],
            code: z.ZodIssueCode.custom,
            message: "Veuillez renseigner la destination d'arrivée"
          })
        }
      }

      // journeyPlace must not be provided when transport is enabled
      if (!!arg.journeyPlaceId) {
        ctx.addIssue({
          path: ['journeyPlaceId'],
          code: z.ZodIssueCode.custom,
          message: "Le lieu de séjour n'est pas autorisé lorsqu'il y a un transport"
        })
      }
    } else {
      // When transport is disabled, journeyPlaceId must be provided
      if (!arg.journeyPlaceId) {
        ctx.addIssue({
          path: ['journeyPlaceId'],
          code: z.ZodIssueCode.custom,
          message: 'Veuillez sélectionner un lieu de séjour pour un voyage sans transport'
        })
      }

      // When no transport, arrival/departure/linkedToMainTripId must NOT be provided
      if (!!arg.arrival) {
        ctx.addIssue({
          path: ['arrival'],
          code: z.ZodIssueCode.custom,
          message: "Le champ 'Lieu d'arrivée' n'est pas autorisé sans transport"
        })
      }

      if (!!arg.departure) {
        ctx.addIssue({
          path: ['departure'],
          code: z.ZodIssueCode.custom,
          message: "Le champ 'Lieu de départ' n'est pas autorisé sans transport"
        })
      }

      if (!!arg.linkedToMainTripId) {
        ctx.addIssue({
          path: ['linkedToMainTripId'],
          code: z.ZodIssueCode.custom,
          message: "La liaison à un voyage principal n'est pas autorisée sans transport"
        })
      }
    }
  })

type TripFormProps = BaseFormProps<TripInput, true> & {
  /* when we have a trip id, we remove it from the list of trips to be linked with as it is the trip we are editing and we don't want to link it to itself */
  tripId?: number
}

const TripForm = ({ defaultValues, tripId, ...validationButtonProps }: TripFormProps) => {
  const form = useForm<TripInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: tripPlaces } = useQuery({
    queryKey: ['trip-places'],
    queryFn: () => getTripPlaces()
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places'],
    queryFn: () => getJourneyPlaces()
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  const { data: tripAddreses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const hasTransport = form.watch('hasTransport')

  const reset = () => {
    form.setValue('transportMeansId', null)
    form.setValue('departure', null)
    form.setValue('arrival', null)
    form.setValue('linkedToMainTripId', null)
    form.setValue('journeyPlaceId', null)
    form.clearErrors()
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-4'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Voyage
          </Typography>

          <div>
            <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
              Transport *
            </Typography>
            <div className="h-2" />
            <div className="flex gap-20 ">
              <FormField
                control={form.control}
                name={'hasTransport'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Avec transport'}
                        checked={field.value === true}
                        onCheck={() => {
                          field.onChange(true)
                          reset()
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'hasTransport'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={'Sans transport'}
                        checked={field.value === false}
                        onCheck={() => {
                          field.onChange(false)
                          reset()
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div>
            <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
              Type de voyage *
            </Typography>
            <div className="h-2" />
            <div className="flex gap-20 ">
              {Object.values(TripTypeEnum).map((type) => (
                <FormField
                  key={type}
                  control={form.control}
                  name={'type'}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={tripTypeMapping[type].label}
                          checked={field.value === type}
                          onCheck={() => field.onChange(type)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'countries'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    variant="primary"
                    label={'Pays de séjour'}
                    options={countries?.data?.map((country) => ({
                      label: country.name,
                      value: country.id
                    }))}
                    selectedOptions={field.value?.map((id) => ({
                      label: countries?.data.find((country) => country.id === id)?.name ?? '',
                      value: id
                    }))}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {hasTransport && (
            <div>
              <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
                Départ - Arrivée
              </Typography>
              <div className="h-3" />
              <div className="flex gap-2 justify-between">
                <FormField
                  control={form.control}
                  name={'departure'}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledSelectInput
                          {...field}
                          required
                          allowNewOption
                          searchable
                          label="Lieu de départ"
                          options={tripPlaces?.data.map((option) => ({
                            label: getTranslatedName(option),
                            value: getTranslatedName(option)
                          }))}
                          selectedOption={getSelectedOption(field.value, field.value)}
                          onChange={(option) => field.onChange(option)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'arrival'}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledSelectInput
                          {...field}
                          required
                          allowNewOption
                          searchable
                          label="Destination d'arrivée"
                          options={tripPlaces?.data.map((option) => ({
                            label: getTranslatedName(option),
                            value: getTranslatedName(option)
                          }))}
                          selectedOption={getSelectedOption(field.value, field.value)}
                          onChange={(option) => field.onChange(option)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {!hasTransport && (
            <div>
              <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
                Lieu de séjour *
              </Typography>
              <div className="h-3" />
              <FormField
                control={form.control}
                name={'journeyPlaceId'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        allowNewOption
                        searchable
                        label="Lieu de séjour"
                        options={journeyPlaces?.data.map((option) => ({
                          label: option.name,
                          value: option.id
                        }))}
                        selectedOption={
                          field.value
                            ? {
                                label:
                                  journeyPlaces?.data.find((option) => option.id === field.value)
                                    ?.name ?? '',
                                value: field.value
                              }
                            : undefined
                        }
                        onChange={(option) => {
                          field.onChange(option)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}

          {hasTransport && (
            <div>
              <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
                Moyens de transport *
              </Typography>
              <div className="h-2" />
              <div className="flex gap-20 ">
                {transportMeans?.data.map((transportMean) => (
                  <FormField
                    key={transportMean.id}
                    control={form.control}
                    name={'transportMeansId'}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={getTranslatedName(transportMean)}
                            checked={field.value === transportMean.id}
                            onCheck={() => field.onChange(transportMean.id)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          )}

          {hasTransport && (
            <FormField
              control={form.control}
              name={'linkedToMainTripId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      searchable
                      label={'Lier à un voyage'}
                      // remove current trip from the list
                      options={trips?.data
                        .filter((trip) => trip.id !== tripId)
                        .map((trip) => ({
                          label: TripProperties.getTripLabel(trip),
                          value: trip.id
                        }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        label: TripProperties.getTripLabel(
                          trips?.data.find((trip) => trip.id === field.value)
                        ),
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name={'foreignProviderArrivalPlaceId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    optional
                    searchable
                    label={"Adresse d'arrivée pour le prestataire étranger"}
                    options={tripAddreses?.data.map((address) => ({
                      label: address.label,
                      value: address.id
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label:
                        tripAddreses?.data.find((address) => address.id === field.value)?.label ??
                        '',
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default TripForm
